/* eslint-disable no-useless-escape */
import React, { useEffect, useContext, useMemo } from 'react'
import { useFormikContext } from 'formik'
import { useEditor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Image from '@tiptap/extension-image'
import { Node } from '@tiptap/core'
import Placeholder from '@tiptap/extension-placeholder'
import CharacterCount from '@tiptap/extension-character-count'
import { ThemeContext } from '@hooks/contexts/ThemeContext/ThemeContext'

import { TiptapCommentProps, TiptapCommentAcceptProps } from './interface'

const emptyValue = '<p></p>'

const EnterShortcutExtension = Node.create({
  addKeyboardShortcuts() {
    return {
      Enter: () => {
        if (this.editor.getHTML() !== emptyValue) return true
        return this.editor.commands.setContent(emptyValue)
      },
    }
  },
})

export function withTiptapComment(Component: React.FC<TiptapCommentProps>) {
  function WithTiptapComment({
    onChange,
    initialValue,
    replyUser,
    value,
    ...props
  }: TiptapCommentAcceptProps) {
    const { theme } = useContext(ThemeContext)
    const { submitForm, isSubmitting } = useFormikContext()

    const editor = useEditor({
      extensions: [
        StarterKit,
        Image.configure({ inline: true }),
        Placeholder.configure({
          placeholder: 'แสดงความคิดเห็น',
        }),
        EnterShortcutExtension,
        CharacterCount,
      ],
      content: initialValue,
      editorProps: {
        handleDOMEvents: {
          keydown: (view, event) => {
            if (event.key === 'Enter' && !event.shiftKey) {
              submitForm()
            }
            return false
          },
          drop: (view, event) => {
            event.preventDefault()
            return true
          },
        },
        transformPastedHTML(html) {
          return html.replace(/<img([\w\W]+?)[\/]*>/g, '')
        },
        handlePaste: (view, event, slice) => {
          const type = ['text', 'heading', 'paragraph']
          return !type.includes(slice.content.firstChild?.type.name || '')
        },
      },
    })

    useEffect(() => {
      if (editor) {
        editor.on('update', () => {
          if (onChange) {
            onChange(
              editor.getHTML(),
              editor.storage.characterCount.characters()
            )
          }
        })
      }
      return () => {
        if (editor) {
          editor.off('update', () => {
            if (onChange) {
              onChange(
                editor.getHTML(),
                editor.storage.characterCount.characters()
              )
            }
          })
          editor.destroy()
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editor])

    useEffect(() => {
      // set content after reset from only
      if (value === '<p></p>') {
        editor?.commands.setContent(value)
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value, editor])

    useEffect(() => {
      if (replyUser?.name) {
        editor?.commands.setContent(`<p>@${replyUser.name}&nbsp;</p>`)
        if (onChange && editor) {
          onChange(editor.getHTML(), editor.storage.characterCount.characters())
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [replyUser, editor])

    if (!editor) return null

    const newProps = {
      theme,
      editor,
      isSubmitting,
      onChange,
      isEmpty:
        editor.storage.characterCount.characters() ===
          editor.getText().length && editor.getText().trim() === '',
      ...props,
    }

    return <Component {...newProps} />
  }
  return WithTiptapComment
}
